import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Drawer,
  Divider,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Event as EventIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Sms as SmsIcon,
  CloudUpload as CloudUploadIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Storefront as StorefrontIcon,
  Groups as GroupsIcon,
  Redeem as RedeemIcon,
} from '@mui/icons-material';
import { LoadingPage } from '../../../components/ui/LoadingSpinner';
import { toast } from 'react-hot-toast';
import apiClient from '../../../lib/api';
import { formatDate, formatAnalyticsCount } from '../../../lib/utils';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import ThemeSelector from '../../../components/ThemeSelector';
import { useAuth } from '../../../context/AuthContext';

// Theme definitions matching obituary site
const getThemeInfo = (themeName) => {
  const themes = {
    teal: {
      name: 'Teal & Sky Blue',
      background: 'linear-gradient(to bottom, #e8f4f8, #f0f9fc)',
    },
    warm: {
      name: 'Brown & Lavender',
      background: 'linear-gradient(to bottom, #f5f0ed, #faf8f5)',
    },
    green: {
      name: 'Lime & Forest',
      background: 'linear-gradient(to bottom, #f0f8ed, #f5faf2)',
    },
    blue: {
      name: 'Bright Blue',
      background: 'linear-gradient(to bottom, #e8f4fd, #f0f9ff)',
    },
    neutral: {
      name: 'Neutral Cream',
      background: 'linear-gradient(to bottom, #e8e8e8, #f5f5f5)',
    },
    pastel: {
      name: 'Sky Blue & Lavender',
      background: 'linear-gradient(to bottom, #f0f4fd, #f8f5ff)',
    },
    gradient: {
      name: 'Vibrant Gradient',
      background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
    },
  };
  return themes[themeName] || themes.neutral;
};

const ObituaryDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  // Handle both routes: /obituaries/:id and /companies/:companyId/branches/:branchId/obituaries/:id
  const id = params.id; // Obituary ID (works for both routes)
  const companyIdFromRoute = params.companyId; // Company ID from nested route (if present)
  const branchIdFromRoute = params.branchId; // Branch ID from nested route (if present)
  const [loading, setLoading] = useState(true);
  const [obituary, setObituary] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showContactsSection, setShowContactsSection] = useState(false);
  const [familyDrawerOpen, setFamilyDrawerOpen] = useState(false);
  const [editingFamilyMemberIndex, setEditingFamilyMemberIndex] = useState(null);
  const [newFamilyMember, setNewFamilyMember] = useState({ firstName: '', lastName: '', relationship: '', phone: '', address: '', address2: '', city: '', state: '', zip: '', optOutOfCards: false });
  const [servicesDrawerOpen, setServicesDrawerOpen] = useState(false);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const EMPTY_SERVICE = { type: '', startDate: '', startTime: '', venueName: '', location: '' };
  const [newService, setNewService] = useState(EMPTY_SERVICE);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsRecipients, setSmsRecipients] = useState([]);
  const [smsSendToAll, setSmsSendToAll] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [deletePhotoDialogOpen, setDeletePhotoDialogOpen] = useState(false);
  const photoInputRef = React.useRef(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(null);
  const [analyticsRefreshing, setAnalyticsRefreshing] = useState(false);
  const { user, currentAccountType } = useAuth();

  useEffect(() => {
    if (id) {
      fetchObituary();
    }
  }, [id]);

  const fetchObituary = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getObituary(id);
      const obituaryData = response.data || response;
      console.log('Obituary data:', obituaryData);
      setObituary(obituaryData);
      
      // Initialize formData
      const initialFormData = {
        deceasedName: obituaryData.deceasedName || '',
        dateOfDeath: obituaryData.dateOfDeath || '',
        url: obituaryData.url || '',
        funeralDate: obituaryData.funeralDate || '',
        funeralTime: obituaryData.funeralTime || '',
        funeralLocation: obituaryData.funeralLocation || '',
        branchId: obituaryData.branchId || '',
        family: obituaryData.family || [],
        theme: obituaryData.theme || 'neutral',
      };
      setFormData(initialFormData);
      setDataInitialized(true);
      
      // Load branch information if branchId exists
      if (obituaryData.branchId) {
        try {
          const branchResponse = await apiClient.getBranch(obituaryData.branchId);
          const branchData = branchResponse.branch || branchResponse.data || branchResponse;
          setBranch(branchData);
        } catch (err) {
          console.error('Error fetching branch:', err);
          // Don't show error toast, branch might not exist
        }
      }
    } catch (error) {
      console.error('Error fetching obituary:', error);
      toast.error('Failed to load obituary');
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalytics = async () => {
    if (!id) return;
    try {
      setAnalyticsRefreshing(true);
      const response = await apiClient.getObituary(id);
      const data = response.data || response;
      setObituary((prev) =>
        prev
          ? {
              ...prev,
              viewed: data.viewed,
              storeVisits: data.storeVisits,
              uniqueStoreVisits: data.uniqueStoreVisits,
              cardsSold: data.cardsSold,
            }
          : data
      );
    } catch (err) {
      console.error('Error refreshing analytics:', err);
      toast.error('Could not refresh analytics');
    } finally {
      setAnalyticsRefreshing(false);
    }
  };

  const handleSave = useCallback(async (isAutoSave = false) => {
    try {
      if (isAutoSave) {
        setAutoSaving(true);
      } else {
        setSaving(true);
      }
      
      const response = await apiClient.updateObituary(id, formData);
      
      if (!isAutoSave) {
        toast.success('Obituary updated successfully');
      }
      
      const updatedObituary = response.data || response;
      setObituary(updatedObituary);
      setLastSaved(new Date());
      
      // Update formData to match updated obituary
      setFormData({
        deceasedName: updatedObituary.deceasedName || '',
        dateOfDeath: updatedObituary.dateOfDeath || '',
        url: updatedObituary.url || '',
        funeralDate: updatedObituary.funeralDate || '',
        funeralTime: updatedObituary.funeralTime || '',
        funeralLocation: updatedObituary.funeralLocation || '',
        branchId: updatedObituary.branchId || '',
        family: updatedObituary.family || [],
        theme: updatedObituary.theme || 'neutral',
      });
      
      // Refresh branch information if branchId changed
      if (updatedObituary.branchId && updatedObituary.branchId.trim() !== '') {
        try {
          const branchResponse = await apiClient.getBranch(updatedObituary.branchId);
          const branchData = branchResponse.branch || branchResponse.data || branchResponse;
          setBranch(branchData);
        } catch (err) {
          console.error('Error fetching branch:', err);
          setBranch(null);
        }
      } else {
        setBranch(null);
      }
      
    } catch (error) {
      console.error('Error updating obituary:', error);
      if (!isAutoSave) {
        toast.error('Failed to update obituary');
      }
    } finally {
      if (isAutoSave) {
        setAutoSaving(false);
      } else {
        setSaving(false);
      }
    }
  }, [id, formData]);

  // Auto-save when formData changes (debounced)
  useEffect(() => {
    // Don't auto-save on initial load
    if (!dataInitialized || !obituary) {
      return;
    }

    // Check if formData has actually changed from the obituary
    const hasChanges = 
      formData.deceasedName !== (obituary.deceasedName || '') ||
      formData.dateOfDeath !== (obituary.dateOfDeath || '') ||
      formData.url !== (obituary.url || '') ||
      formData.funeralDate !== (obituary.funeralDate || '') ||
      formData.funeralTime !== (obituary.funeralTime || '') ||
      formData.funeralLocation !== (obituary.funeralLocation || '') ||
      formData.branchId !== (obituary.branchId || '') ||
      formData.theme !== (obituary.theme || 'neutral') ||
      JSON.stringify(formData.family || []) !== JSON.stringify(obituary.family || []);

    if (!hasChanges) {
      return;
    }

    // Debounce auto-save: wait 1 second after user stops typing
    const timer = setTimeout(() => {
      handleSave(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData, dataInitialized, obituary, id, handleSave]);

  // Load branches when obituary is loaded
  useEffect(() => {
    const loadBranches = async () => {
      if (obituary) {
        // Determine companyId - use obituary's companyId or user's companyId
        const companyId = obituary.companyId || user?.companyId;
        
        if (companyId) {
          try {
            const resp = await apiClient.getBranches({ companyId, page: 1, limit: 100 });
            // If admin emulating staff, show all branches. Otherwise filter by user access
            if (user?.type === 'admin' && currentAccountType === 'staff') {
              setBranches(resp.data || []);
            } else if (currentAccountType === 'staff' && user?.companyId) {
              const userBranches = user.allBranches 
                ? (resp.data || [])
                : (resp.data || []).filter(b => 
                    user.branchIds && user.branchIds.includes(b.id)
                  );
              setBranches(userBranches);
            } else {
              // Admin or funeral director - show all branches for the company
              setBranches(resp.data || []);
            }
          } catch (err) {
            console.error('Failed to load branches', err);
            setBranches([]);
          }
        } else if (user?.type === 'admin') {
          // Admin without company context - load all branches
          try {
            const resp = await apiClient.getBranches({ page: 1, limit: 100 });
            setBranches(resp.data || []);
          } catch (err) {
            console.error('Failed to load branches', err);
            setBranches([]);
          }
        }
      }
    };
    loadBranches();
  }, [obituary, user, currentAccountType]);

  const handleAddFamilyMember = () => {
    setNewFamilyMember({ firstName: '', lastName: '', relationship: '', phone: '', address: '', city: '', state: '', zip: '', optOutOfCards: false });
    setFamilyDrawerOpen(true);
  };

  const handleSaveFamilyMember = () => {
    // Validate required fields
    if (!newFamilyMember.firstName || !newFamilyMember.lastName) {
      toast.error('First name and last name are required');
      return;
    }
    
    if (!newFamilyMember.relationship || !newFamilyMember.relationship.trim()) {
      toast.error('Relationship is required');
      return;
    }
    
    if (!newFamilyMember.phone || !newFamilyMember.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    
    // Only require address fields if not opting out of receiving cards
    if (!newFamilyMember.optOutOfCards) {
      if (!newFamilyMember.address || !newFamilyMember.city || !newFamilyMember.state || !newFamilyMember.zip) {
        toast.error('Address, city, state, and zip code are required when opting in to receive cards');
        return;
      }
    }
    
    // Update formData with the new family member - autosave will handle the API call
    const family = formData.family || [];
    let updatedFamily;
    
    if (editingFamilyMemberIndex !== null) {
      // Editing existing member
      updatedFamily = [...family];
      updatedFamily[editingFamilyMemberIndex] = newFamilyMember;
    } else {
      // Adding new member
      updatedFamily = [...family, newFamilyMember];
    }
    
    // Update formData - this will trigger autosave
    // Don't update obituary state here - let autosave handle it after the API call succeeds
    setFormData(prev => ({
      ...prev,
      family: updatedFamily
    }));
    
    toast.success(editingFamilyMemberIndex !== null ? 'Family member updated successfully' : 'Family member added successfully');
    setFamilyDrawerOpen(false);
    setEditingFamilyMemberIndex(null);
    setNewFamilyMember({ firstName: '', lastName: '', relationship: '', phone: '', address: '', city: '', state: '', zip: '', optOutOfCards: false });
  };

  const handleEditFamilyMember = (index) => {
    const family = obituary.family || [];
    setNewFamilyMember({ 
      firstName: family[index].firstName || '',
      lastName: family[index].lastName || '',
      relationship: family[index].relationship || '',
      phone: family[index].phone || '',
      address: family[index].address || '',
      address2: family[index].address2 || '',
      city: family[index].city || '',
      state: family[index].state || '',
      zip: family[index].zip || '',
      optOutOfCards: family[index].optOutOfCards || false
    });
    setEditingFamilyMemberIndex(index);
    setFamilyDrawerOpen(true);
  };

  const handleDeleteFamilyMember = async (index) => {
    if (!window.confirm('Are you sure you want to delete this family member?')) {
      return;
    }
    
    try {
      // Get the family member to delete from obituary (where the index comes from)
      const obituaryFamily = obituary.family || [];
      const memberToDelete = obituaryFamily[index];
      
      if (!memberToDelete) {
        toast.error('Family member not found');
        return;
      }
      
      // Find and remove the matching member from formData by matching key fields
      const formDataFamily = formData.family || [];
      const updatedFamily = formDataFamily.filter(member => 
        !(member.firstName === memberToDelete.firstName &&
          member.lastName === memberToDelete.lastName &&
          member.phone === memberToDelete.phone &&
          member.relationship === memberToDelete.relationship)
      );
      
      // Update formData with the removed family member
      const updatedFormData = {
        ...formData,
        family: updatedFamily
      };
      
      setFormData(updatedFormData);
      
      // Also update obituary state immediately for UI feedback
      setObituary(prev => ({
        ...prev,
        family: updatedFamily
      }));
      
      toast.success('Family member deleted successfully');
      setFamilyDrawerOpen(false);
      setEditingFamilyMemberIndex(null);
      setNewFamilyMember({ firstName: '', lastName: '', relationship: '', phone: '', address: '', city: '', state: '', zip: '', optOutOfCards: false });
      
      // Force immediate save for delete operations (don't wait for debounce)
      // Use updatedFormData directly to ensure we save the correct state
      const response = await apiClient.updateObituary(id, updatedFormData);
      const updatedObituary = response.data || response;
      setObituary(updatedObituary);
      setFormData({
        deceasedName: updatedObituary.deceasedName || '',
        dateOfDeath: updatedObituary.dateOfDeath || '',
        url: updatedObituary.url || '',
        funeralDate: updatedObituary.funeralDate || '',
        funeralTime: updatedObituary.funeralTime || '',
        funeralLocation: updatedObituary.funeralLocation || '',
        branchId: updatedObituary.branchId || '',
        family: updatedObituary.family || [],
        theme: updatedObituary.theme || 'neutral',
      });
    } catch (error) {
      console.error('Error deleting family member:', error);
      toast.error('Failed to delete family member');
    }
  };


  const handleAddService = () => {
    setNewService(EMPTY_SERVICE);
    setEditingServiceIndex(null);
    setServicesDrawerOpen(true);
  };

  const handleSaveService = async () => {
    try {
      const services = obituary.services || [];
      let updatedServices;
      
      const normalizedService = {
        ...newService,
        venueName: newService.venueName || newService.venue || '',
        venue: newService.venueName || newService.venue || '',
      };

      if (editingServiceIndex !== null) {
        // Editing existing service
        updatedServices = [...services];
        updatedServices[editingServiceIndex] = normalizedService;
      } else {
        // Adding new service
        updatedServices = [...services, normalizedService];
      }
      
      const response = await apiClient.updateObituary(id, { services: updatedServices });
      toast.success(editingServiceIndex !== null ? 'Service updated successfully' : 'Service added successfully');
      setObituary(response.data || response);
      setServicesDrawerOpen(false);
      setEditingServiceIndex(null);
      setNewService(EMPTY_SERVICE);
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    }
  };

  const handleEditService = (index) => {
    const services = obituary.services || [];
    const service = services[index] || {};
    setNewService({
      ...EMPTY_SERVICE,
      ...service,
      venueName: service.venueName || service.venue || '',
    });
    setEditingServiceIndex(index);
    setServicesDrawerOpen(true);
  };

  const handleDeleteService = async (index) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }
    
    try {
      const services = obituary.services || [];
      const updatedServices = services.filter((_, i) => i !== index);
      const response = await apiClient.updateObituary(id, { services: updatedServices });
      toast.success('Service deleted successfully');
      setObituary(response.data || response);
      setServicesDrawerOpen(false);
      setEditingServiceIndex(null);
      setNewService(EMPTY_SERVICE);
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const getObituaryLink = () => {
    // Use the new obituary service with hash-based routing
    const obituaryBaseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://obituaries.me'
      : 'http://localhost:3004';
    return `${obituaryBaseUrl}#${id}`;
  };

  const handleOpenSMSDialog = (memberPhone = null, sendToAll = false) => {
    if (sendToAll) {
      // Check if there are any family members with phone numbers
      const familyWithPhones = (obituary.family || []).filter(m => m.phone && m.phone.trim());
      
      if (familyWithPhones.length === 0) {
        toast.error('No family members have phone numbers');
        return;
      }
      setSmsSendToAll(true);
      setSmsRecipients([]);
    } else {
      setSmsSendToAll(false);
      setSmsRecipients(memberPhone ? [memberPhone] : []);
    }
    // Pre-populate message without obituary link (link will be appended automatically)
    const deceasedName = obituary.deceasedName || 'the deceased';
    setSmsMessage(`Hi everyone, here are the funeral details for ${deceasedName}:\n\nPlease help by sharing with others.`);
    setSmsDialogOpen(true);
  };

  const handleCloseSMSDialog = () => {
    setSmsDialogOpen(false);
    setSmsMessage('');
    setSmsRecipients([]);
    setSmsSendToAll(false);
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      const response = await apiClient.uploadObituaryPhoto(id, file);
      
      if (response.success && response.data) {
        // Refresh obituary data to get updated photo
        await fetchObituary();
        toast.success('Photo uploaded successfully');
      } else {
        toast.error(response.error || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handlePhotoUploadClick = () => {
    photoInputRef.current?.click();
  };

  const handleDeletePhoto = async () => {
    if (!id || !obituary.photoId) return;
    
    try {
      setDeletingPhoto(true);
      await apiClient.deleteObituaryPhoto(id);
      
      // Refresh obituary data to remove photo
      await fetchObituary();
      toast.success('Photo deleted successfully');
      setDeletePhotoDialogOpen(false);
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error(error.response?.data?.error || 'Failed to delete photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  const detectLinks = (text) => {
    // Pattern to detect URLs (http://, https://, www., and common TLDs)
    const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/gi;
    const matches = text.match(urlPattern);
    return matches || [];
  };

  const handleSendSMS = async () => {
    if (!smsSendToAll && smsRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    // Check for any links in the message - no links allowed, obituary link will be added automatically
    const detectedLinks = detectLinks(smsMessage);
    
    if (detectedLinks.length > 0) {
      toast.error('Links are not allowed in the message. The obituary link will be added automatically.');
      return;
    }

    // Ensure the obituary link is always included in the message
    const obituaryLink = getObituaryLink();
    let finalMessage = smsMessage.trim();
    const deceasedName = obituary.deceasedName || 'the deceased';
    
    // Always ensure the obituary link is in the message
    if (!finalMessage.includes(obituaryLink)) {
      if (finalMessage) {
        // Append link on its own line if message exists but doesn't have link
        finalMessage = `${finalMessage}\n\n${obituaryLink}`;
      } else {
        // Use default format if message is empty
        finalMessage = `Hi everyone, here are the funeral details for ${deceasedName}:\n\n${obituaryLink}\n\nPlease help by sharing with others.`;
      }
    }

    if (!finalMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSendingSMS(true);
      const response = await apiClient.sendSMSToFamily(id, {
        message: finalMessage,
        phoneNumbers: smsSendToAll ? [] : smsRecipients,
        sendToAll: smsSendToAll,
      });

      if (response.success) {
        toast.success(response.message || 'SMS sent successfully');
        
        // Mark obituary as completed
        setObituary(prev => ({
          ...prev,
          mmsSent: true,
          mmsSentAt: new Date().toISOString()
        }));
        
        handleCloseSMSDialog();
      } else {
        toast.error(response.error || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSendingSMS(false);
    }
  };

  if (loading) return <LoadingPage />;

  if (!obituary) {
    return (
      <DashboardLayout title="OBITUARY DETAILS">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">Obituary not found</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout title="OBITUARY DETAILS">
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4">Edit Obituary</Typography>
              {lastSaved ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {autoSaving ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={12} />
                      Auto-saving...
                    </Box>
                  ) : (
                    `Last saved: ${lastSaved.toLocaleTimeString()}`
                  )}
                </Typography>
              ) : (
                autoSaving && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={12} />
                      Auto-saving...
                    </Box>
                  </Typography>
                )
              )}
            </Box>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              endIcon={<OpenInNewIcon />}
              onClick={() => {
                // Open public obituary page in new tab using hash-based routing
                const obituaryBaseUrl = process.env.NODE_ENV === 'production' 
                  ? 'https://obituaries.me'
                  : 'http://localhost:3004';
                const obituaryUrl = `${obituaryBaseUrl}#${id}`;
                window.open(obituaryUrl, '_blank');
              }}
            >
              View Obituary
            </Button>
          </Box>

          {/* Analytics — same gradient stat-card pattern as Obituary Management & other dashboards */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
                  Public obituary page views, memorial store activity, and sympathy cards sold for this notice.
                </Typography>
              </Box>
              <Tooltip title="Reload latest counts from the server">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={refreshAnalytics}
                    disabled={analyticsRefreshing}
                    startIcon={
                      analyticsRefreshing ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                  >
                    Refresh
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2,
            }}>
              <Card sx={{
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4a8f 100%)',
                color: 'white',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <VisibilityIcon sx={{ mr: 1, fontSize: 24 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Page views
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {formatAnalyticsCount(obituary.viewed)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.85 }}>
                    Public obituary opens
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{
                background: 'linear-gradient(135deg, #4a9ee8 0%, #00d8e0 100%)',
                color: 'white',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StorefrontIcon sx={{ mr: 1, fontSize: 24 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Store visits
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {formatAnalyticsCount(obituary.storeVisits)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.85 }}>
                    Memorial store sessions (total)
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{
                background: 'linear-gradient(135deg, #d885e8 0%, #e04a5f 100%)',
                color: 'white',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupsIcon sx={{ mr: 1, fontSize: 24 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Unique visitors
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {formatAnalyticsCount(obituary.uniqueStoreVisits)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.85 }}>
                    Store visitors (30-day dedupe)
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{
                background: 'linear-gradient(135deg, #3dd06f 0%, #32e0c0 100%)',
                color: 'white',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <RedeemIcon sx={{ mr: 1, fontSize: 24 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Cards sold
                    </Typography>
                  </Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {formatAnalyticsCount(obituary.cardsSold)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.85 }}>
                    Sympathy cards purchased (qty)
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Progress Indicator */}
          {obituary && (() => {
            // Use formData for Basic Info since it's editable, obituary for others
            const basicInfoComplete = !!(formData.deceasedName && formData.dateOfDeath && (obituary.photoUrl || obituary.photoId));
            
            const steps = [
              { 
                label: 'Basic Info', 
                complete: basicInfoComplete,
                getMissingText: () => {
                  const missing = [];
                  // Required fields
                  if (!formData.deceasedName || !formData.deceasedName.trim()) {
                    missing.push('Deceased Name');
                  }
                  if (!formData.dateOfDeath || !formData.dateOfDeath.trim()) {
                    missing.push('Date of Death');
                  }
                  // Optional but recommended fields
                  if (branches.length > 0 && (!formData.branchId || !formData.branchId.trim())) {
                    missing.push('Branch');
                  }
                  if (!formData.url || !formData.url.trim()) {
                    missing.push('URL');
                  }
                  if (!obituary.photoUrl && !obituary.photoId) {
                    missing.push('Photo');
                  }
                  if (missing.length === 0) return 'Complete';
                  return missing;
                }
              },
              { 
                label: 'Family', 
                complete: (obituary.family?.length || 0) > 0,
                getMissingText: () => {
                  return (obituary.family?.length || 0) === 0 ? 'Missing: Add at least one family member' : 'Complete';
                }
              },
              { 
                label: 'Services', 
                complete: (obituary.services?.length || 0) > 0,
                getMissingText: () => {
                  return (obituary.services?.length || 0) === 0 ? 'Missing: Add at least one service' : 'Complete';
                }
              }
            ];
            const completedSteps = steps.filter(s => s.complete).length;
            const percentComplete = Math.round((completedSteps / steps.length) * 100);
            const canSendMMS = steps[0].complete && steps[1].complete;
            
            return (
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Setup Progress
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {percentComplete}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {steps.map((step, idx) => {
                        const missingInfo = step.getMissingText();
                        const tooltipContent = Array.isArray(missingInfo) ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Missing:</Typography>
                            {missingInfo.map((item, itemIdx) => (
                              <Typography key={itemIdx} variant="body2" sx={{ display: 'block' }}>
                                • {item}
                              </Typography>
                            ))}
                          </Box>
                        ) : missingInfo;
                        
                        return (
                          <React.Fragment key={idx}>
                            <Tooltip title={tooltipContent} arrow>
                              <Chip
                                label={step.label}
                                color={step.complete ? 'success' : 'default'}
                                size="small"
                                icon={step.complete ? <CheckIcon /> : undefined}
                                sx={{ 
                                  fontWeight: step.complete ? 600 : 400,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8
                                  }
                                }}
                                onClick={() => {
                                  // Scroll to the relevant section
                                  if (idx === 0) {
                                    // Scroll to Basic Information section
                                    const basicInfoSection = document.querySelector('[data-section="basic-info"]');
                                    basicInfoSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  } else if (idx === 1) {
                                    // Scroll to Family section
                                    const familySection = document.querySelector('[data-section="family"]');
                                    familySection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  } else if (idx === 2) {
                                    // Scroll to Services section
                                    const servicesSection = document.querySelector('[data-section="services"]');
                                    servicesSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }}
                              />
                            </Tooltip>
                            {idx < steps.length - 1 && <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Box>
                  <Button 
                    variant="contained" 
                    startIcon={<SmsIcon />} 
                    onClick={() => handleOpenSMSDialog(null, true)}
                    disabled={!canSendMMS || !obituary.family?.some(m => m.phone && m.phone.trim())}
                    size="small"
                  >
                    Send SMS to Family
                  </Button>
                </Box>
              </Box>
            );
          })()}

          {/* Basic Information Section */}
          <Card sx={{ mb: 3 }} data-section="basic-info">
            <CardHeader title="Basic Information" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {branches.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      value={formData.branchId || ''}
                      label="Branch"
                      onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>No branch</em>
                      </MenuItem>
                      {branches.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.branchName || 'Unnamed Branch'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <TextField
                  fullWidth
                  label="Deceased Name"
                  value={formData.deceasedName}
                  onChange={(e) => setFormData({ ...formData, deceasedName: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  label="Date of Death"
                  type="date"
                  value={formData.dateOfDeath}
                  onChange={(e) => setFormData({ ...formData, dateOfDeath: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="URL"
                  value={formData.url || ''}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="Enter obituary URL"
                />
                
                {/* Photo Upload */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Photo</Typography>
                    {(obituary.photoUrl || obituary.photoId) && (
                      <Tooltip title="Delete Photo">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeletePhotoDialogOpen(true)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    {/* File Preview */}
                    {obituary.photoUrl || obituary.photoId ? (
                      <Box sx={{ 
                        width: { xs: '100%', sm: 200 }, 
                        maxWidth: { xs: '100%', sm: 200 },
                        border: '2px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        position: 'relative',
                        boxShadow: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={handlePhotoUploadClick}
                      >
                        <img
                          src={apiClient.getObituaryPhotoUrl(id)}
                          alt={obituary.deceasedName}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '300px',
                            height: 'auto',
                            width: 'auto',
                            objectFit: 'contain',
                          }}
                          onError={(e) => {
                            console.error('Error loading photo:', e);
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <input
                          type="file"
                          ref={photoInputRef}
                          onChange={handlePhotoUpload}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                        <Tooltip title="Click to upload a photo" placement="top">
                          <Box 
                            onClick={handlePhotoUploadClick}
                            sx={{ 
                              width: { xs: '100%', sm: 120 }, 
                              height: { xs: 150, sm: 180 },
                              border: '2px dashed',
                              borderColor: 'primary.main',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'primary.50',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: 'primary.100',
                                borderColor: 'primary.dark',
                                transform: 'scale(1.02)'
                              }
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              textAlign: 'center',
                              p: 2,
                              gap: 1
                            }}>
                              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.6 }} />
                              <Typography variant="body2" color="primary.main">
                                No photo uploaded
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Click here to upload a photo
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
                
                <ThemeSelector 
                  selectedTheme={formData.theme || 'neutral'} 
                  onThemeSelect={(theme) => setFormData({ ...formData, theme })} 
                />
              </Box>
            </CardContent>
          </Card>

          {/* Surviving Friends and Family */}
          <Card sx={{ mb: 3 }} data-section="family">
            <CardHeader 
              title="Surviving Friends and Family"
              action={
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddFamilyMember}>
                  Add
                </Button>
              }
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Family members added here will be automatically added to the store to receive gifts from visitors.
                </Typography>
              </Alert>
              {(obituary.family && obituary.family.length > 0) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {obituary.family.map((member, index) => (
                    <Box key={index} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {member.firstName} {member.lastName}
                        </Typography>
                        {member.relationship && <Typography variant="body2" color="text.secondary">Relationship: {member.relationship}</Typography>}
                        {member.phone && <Typography variant="body2">Phone: {member.phone}</Typography>}
                        {member.optOutOfCards && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Opted out of receiving cards
                          </Typography>
                        )}
                        {(member.address || member.city || member.state || member.zip) && (
                          <Typography variant="body2">
                            Address: {[member.address, member.city, member.state, member.zip].filter(Boolean).join(', ')}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {member.phone && member.phone.trim() && (
                          <Tooltip title="Send SMS">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSMSDialog(member.phone, false)} 
                              color="primary"
                            >
                              <SmsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditFamilyMember(index)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No family members added yet. Click Edit to add family members.</Typography>
              )}
            </CardContent>
          </Card>


          {/* Services */}
          <Card sx={{ mb: 3 }} data-section="services">
            <CardHeader 
              title="Services"
              action={
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddService}>
                  Add
                </Button>
              }
            />
            <CardContent>
              {(obituary.services && obituary.services.length > 0) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {obituary.services.map((service, index) => (
                    <Box key={index} sx={{ borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <EventIcon fontSize="small" color="secondary" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {service.type || `Service ${index + 1}`}
                          </Typography>
                        </Box>
                        {service.startDate && (
                          <Typography variant="body2">
                            <strong>Date:</strong> {formatDate(service.startDate)}
                          </Typography>
                        )}
                        {service.startTime && (
                          <Typography variant="body2">
                            <strong>Time:</strong> {service.startTime}
                          </Typography>
                        )}
                        {service.venueName && (
                          <Typography variant="body2">
                            <strong>Venue Name:</strong> {service.venueName}
                          </Typography>
                        )}
                        {!service.venueName && service.venue && (
                          <Typography variant="body2">
                            <strong>Venue Name:</strong> {service.venue}
                          </Typography>
                        )}
                        {service.location && (
                          <Typography variant="body2">
                            <strong>Location:</strong> {service.location}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleEditService(index)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteService(index)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No services added yet. Click "Add Service" to add a service.</Typography>
              )}
            </CardContent>
          </Card>

        </Box>
      </DashboardLayout>


      {/* Family Member Drawer */}
      <Drawer anchor="right" open={familyDrawerOpen} onClose={() => {
        setFamilyDrawerOpen(false);
        setEditingFamilyMemberIndex(null);
        setNewFamilyMember({ firstName: '', lastName: '', relationship: '', phone: '', address: '', city: '', state: '', zip: '', optOutOfCards: false });
      }} PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3, backgroundColor: 'background.paper' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{editingFamilyMemberIndex !== null ? 'Edit Family Member' : 'Add Family Member'}</Typography>
          <IconButton onClick={() => {
            setFamilyDrawerOpen(false);
            setEditingFamilyMemberIndex(null);
            setNewFamilyMember({ firstName: '', lastName: '', relationship: '', phone: '', address: '', address2: '', city: '', state: '', zip: '', optOutOfCards: false });
          }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField fullWidth label="First Name" value={newFamilyMember.firstName} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, firstName: e.target.value })} required />
          <TextField fullWidth label="Last Name" value={newFamilyMember.lastName} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, lastName: e.target.value })} required />
          <Autocomplete
            freeSolo
            options={[
              'Spouse',
              'Child',
              'Son',
              'Daughter',
              'Parent',
              'Father',
              'Mother',
              'Sibling',
              'Brother',
              'Sister',
              'Grandchild',
              'Grandson',
              'Granddaughter',
              'Grandparent',
              'Grandfather',
              'Grandmother',
              'Friend',
              'Cousin',
              'Aunt',
              'Uncle',
              'Niece',
              'Nephew',
              'Other'
            ]}
            value={newFamilyMember.relationship || ''}
            onChange={(event, newValue) => {
              setNewFamilyMember({ ...newFamilyMember, relationship: newValue || '' });
            }}
            onInputChange={(event, newInputValue) => {
              setNewFamilyMember({ ...newFamilyMember, relationship: newInputValue });
            }}
            renderInput={(params) => (
              <TextField {...params} label="Relationship" placeholder="Select or type relationship" required />
            )}
          />
          <TextField fullWidth label="Phone" value={newFamilyMember.phone} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, phone: e.target.value })} required />
          <TextField fullWidth label="Address" value={newFamilyMember.address} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, address: e.target.value })} required={!newFamilyMember.optOutOfCards} disabled={newFamilyMember.optOutOfCards} />
          <TextField fullWidth label="Address 2" value={newFamilyMember.address2 || ''} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, address2: e.target.value })} disabled={newFamilyMember.optOutOfCards} />
          <TextField fullWidth label="City" value={newFamilyMember.city} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, city: e.target.value })} required={!newFamilyMember.optOutOfCards} disabled={newFamilyMember.optOutOfCards} />
          <TextField fullWidth label="State" value={newFamilyMember.state} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, state: e.target.value })} required={!newFamilyMember.optOutOfCards} disabled={newFamilyMember.optOutOfCards} />
          <TextField fullWidth label="Zip" value={newFamilyMember.zip} onChange={(e) => setNewFamilyMember({ ...newFamilyMember, zip: e.target.value })} required={!newFamilyMember.optOutOfCards} disabled={newFamilyMember.optOutOfCards} />
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            control={
              <Switch
                checked={newFamilyMember.optOutOfCards || false}
                onChange={(e) => setNewFamilyMember({ ...newFamilyMember, optOutOfCards: e.target.checked })}
              />
            }
            label="Opt out of receiving cards"
          />
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveFamilyMember} fullWidth>
            {editingFamilyMemberIndex !== null ? 'Update Member' : 'Add Member'}
          </Button>
          <Button variant="outlined" onClick={() => {
            setFamilyDrawerOpen(false);
            setEditingFamilyMemberIndex(null);
            setNewFamilyMember({ firstName: '', lastName: '', relationship: '', phone: '', address: '', address2: '', city: '', state: '', zip: '', optOutOfCards: false });
          }} fullWidth>Cancel</Button>
          {editingFamilyMemberIndex !== null && (
            <>
              <Divider sx={{ my: 1 }} />
              <Button 
                fullWidth
                variant="contained" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={() => handleDeleteFamilyMember(editingFamilyMemberIndex)}
              >
                Delete Family Member
              </Button>
            </>
          )}
        </Box>
      </Drawer>


      {/* Service Drawer */}
      <Drawer anchor="right" open={servicesDrawerOpen} onClose={() => {
        setServicesDrawerOpen(false);
        setEditingServiceIndex(null);
        setNewService(EMPTY_SERVICE);
      }} PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3, backgroundColor: 'background.paper', overflow: 'visible' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{editingServiceIndex !== null ? 'Edit Service' : 'Add Service'}</Typography>
          <IconButton onClick={() => {
            setServicesDrawerOpen(false);
            setEditingServiceIndex(null);
            setNewService(EMPTY_SERVICE);
          }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Autocomplete
            freeSolo
            options={[
              'Funeral Service',
              'Visitation',
              'Memorial Service',
              'Graveside Service',
              'Celebration of Life',
              'Wake',
              'Viewing',
              'Private Family Services',
            ]}
            value={newService.type}
            onInputChange={(event, newInputValue) => {
              setNewService({ ...newService, type: newInputValue });
            }}
            onChange={(event, newValue) => {
              setNewService({ ...newService, type: newValue || '' });
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Service Type" 
                required
              />
            )}
          />
          <TextField 
            fullWidth 
            label="Start Date" 
            type="date" 
            value={newService.startDate} 
            onChange={(e) => setNewService({ ...newService, startDate: e.target.value })} 
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField 
            fullWidth 
            label="Start Time" 
            type="time" 
            value={newService.startTime} 
            onChange={(e) => setNewService({ ...newService, startTime: e.target.value })} 
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="Venue Name"
            value={newService.venueName || ''}
            onChange={(e) => setNewService({ ...newService, venueName: e.target.value })}
            placeholder="e.g. St. Mark Chapel"
          />
          <GooglePlacesAutocomplete 
            key={`service-location-${editingServiceIndex !== null ? editingServiceIndex : 'new'}-${servicesDrawerOpen}`}
            label="Location" 
            value={newService.location} 
            onChange={(e) => setNewService({ ...newService, location: e.target.value })} 
            onSelect={(place) => {
              if (place && place.formatted_address) {
                setNewService({ ...newService, location: place.formatted_address });
              }
            }}
            required
            placeholder="Enter a location address..."
          />
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveService} fullWidth>
            {editingServiceIndex !== null ? 'Update Service' : 'Add Service'}
          </Button>
          <Button variant="outlined" onClick={() => {
            setServicesDrawerOpen(false);
            setEditingServiceIndex(null);
            setNewService(EMPTY_SERVICE);
          }} fullWidth>Cancel</Button>
          {editingServiceIndex !== null && (
            <>
              <Divider sx={{ my: 1 }} />
              <Button 
                fullWidth
                variant="contained" 
                color="error" 
                startIcon={<DeleteIcon />} 
                onClick={() => handleDeleteService(editingServiceIndex)}
              >
                Delete Service
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      {/* SMS Dialog */}
      <Dialog open={smsDialogOpen} onClose={handleCloseSMSDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {smsSendToAll 
            ? 'Send SMS to All Family Members'
            : 'Send SMS'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {smsSendToAll ? (
              <Typography variant="body2" color="text.secondary">
                This message will be sent to all family members with phone numbers:
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {(obituary.family || []).filter(m => m.phone && m.phone.trim()).map((member, idx) => (
                    <li key={idx}>
                      {member.firstName} {member.lastName} - {member.phone}
                    </li>
                  ))}
                </Box>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Recipient: {smsRecipients[0]}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Message"
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              placeholder="Enter your message here..."
              disabled={sendingSMS}
              helperText={
                (() => {
                  const detectedLinks = detectLinks(smsMessage);
                  if (detectedLinks.length > 0) {
                    return 'Links are not allowed. The obituary link will be added automatically.';
                  }
                  return '';
                })()
              }
              error={
                (() => {
                  const detectedLinks = detectLinks(smsMessage);
                  return detectedLinks.length > 0;
                })()
              }
            />
            
            {/* Message Preview */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Message Preview
              </Typography>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'background.paper',
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                {/* Message Text */}
                <Box>
                  {(() => {
                    const obituaryLink = getObituaryLink();
                    let previewMessage = smsMessage.trim();
                    const deceasedName = obituary.deceasedName || 'the deceased';
                    
                    // Show how the final message will look
                    if (!previewMessage.includes(obituaryLink)) {
                      if (previewMessage) {
                        // Append link on its own line if message exists but doesn't have link
                        previewMessage = `${previewMessage}\n\n${obituaryLink}`;
                      } else {
                        // Use default format if message is empty
                        previewMessage = `Hi everyone, here are the funeral details for ${deceasedName}:\n\n${obituaryLink}\n\nPlease help by sharing with others.`;
                      }
                    }
                    
                    return (
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.6,
                        }}
                      >
                        {previewMessage || 'Enter a message above...'}
                      </Typography>
                    );
                  })()}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSMSDialog} disabled={sendingSMS}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendSMS} 
            variant="contained"
            startIcon={sendingSMS ? <CircularProgress size={20} /> : <SmsIcon />}
            disabled={sendingSMS || !smsMessage.trim()}
          >
            {sendingSMS ? 'Sending...' : 'Send SMS'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Photo Confirmation Dialog */}
      <Dialog
        open={deletePhotoDialogOpen}
        onClose={() => setDeletePhotoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Photo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this photo? This action cannot be undone and will permanently remove the photo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletePhotoDialogOpen(false)}
            disabled={deletingPhoto}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeletePhoto}
            color="error"
            variant="contained"
            disabled={deletingPhoto}
            startIcon={deletingPhoto ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingPhoto ? 'Deleting...' : 'Delete Photo'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ObituaryDetailPage;

